// 182 A.3 [RULED 2026-09-03, Laurent, 167 R6] — THE NOTES SECTION: THE CONTACT
// TIMELINE, ONE COMPONENT, THREE MOUNTS.
//
// The `Interaction` table (182 A.1) read and written through /api/interactions
// (182 A.2): a note somebody typed, a chat message logged as one — newest
// first. 186 B.7b [RULED 186 (10)]: the section HOLDS its rows — ONE fetch at
// the store's cap (200), no cursor — and pages them with the app's pager
// (hooks/usePager + components/Pager, the chronology's mechanism, extracted in
// B.7a), ten to a page. (183 E.1: the archived e-mail and the
// imported WhatsApp conversation no longer appear here — Notes are hand-written
// notes only.) Mounted in CaseView and CollectiveCaseView directly after
// the timeline (the timeline is the AUDIT trail, this is the CONTACT trail,
// side by side) and in the client record beside the Cases card, where
// `includeMatters` widens the list to the person's matters and each matter row
// carries its pill — "what did we last say to Peeters" reads one list.
//
// A STABLE, MODULE-SCOPE COMPONENT (the ClientCasesCard rule): the hooks live
// here, never in a render body — the EList detail branch cannot hold hooks and
// a component defined inline remounts on every App render (Bug A).
//
// WHAT THE HOST DECIDES, AND WHAT THIS DOES NOT. The host renders this only for
// a viewer holding `caseView.notes.view` (a person's record: `lateral.persons.view`)
// and hands the write bits down; this shows the composer for `canCreate` and
// the archive on the viewer's OWN notes for `canEdit` (an admin: any note).
// Minted rows show neither — the server answers 409 interaction.minted anyway;
// the record they mirror lives elsewhere.
//
// 186 B.2 [RULED 186 (5)] — NOTES AND CHAT ROWS ARE HISTORY, NOT DRAFTS. Modify
// went entirely (a note is not edited retroactively) and Delete with it: a
// note is ARCHIVED, never erased — `PUT /:id` is the archive verb now.
//
// THE DATE IS THE NOTE'S, NOT THE CLOCK'S. A note about last week's call carries
// last week's date: the modal's day field is prefilled with today and
// editable, and the list sorts by that date. Today keeps the wall clock; another
// day is written at local noon, so a re-dated note lands inside its day rather
// than at midnight, where a time zone would move it to the day before.
//
// 186 B.3 [RULED 186 (6)] — THE SECTION IS A LIST, NOT A FORM: the standing
// composer is gone; "+ Add" beside the lock opens InteractionModal (date, text,
// record), mounted here, the way CaseEmailsSection mounts AttachmentSaveModal.
//
// MEASURED: the tree holds no shared date+time formatter — `DateField.dmy2` is
// day-only, the timeline uses `toLocaleString`, the chat a relative form — so
// the `dd.mm.yyyy hh:mm` the brief names is composed here, once.
//
// THE OWNER CHANGE RESETS STATE DURING RENDER, not inside the effect: React's
// own "adjust state when a prop changes" shape, so the effect does nothing but
// fetch and set state asynchronously (react-hooks/set-state-in-effect).

import React, { useEffect, useState } from "react";
import { Archive } from "lucide-react";
import { useTranslation } from "../i18n";
import {
  getInteractions,
  createInteractionNote,
  archiveInteractionNote,
  unarchiveInteractionNote,
  INTERACTIONS_CHANGED_EVENT,
} from "../api";
import type { InteractionRow, InteractionOwner } from "../api";
import type { Translate } from "../lib/interestRefusal";
import { SectionHeader, LockButton, AddButton } from "./SharedUI";
import { useConfirm } from "./useConfirmAndUndo";
import InteractionModal from "./modals/InteractionModal";
import SectionSearch from "./SectionSearch";
import { usePager, pageOf } from "../hooks/usePager";
import Pager from "./Pager";
import { FS } from "../lib/tokens";

/* 186 B.7b — ONE fetch, asking for the store's cap (`MAX_LIMIT = 200`,
   interactionStore.js; the route clamps anything higher to it). Nothing on a
   matter approaches 200 notes; the only mount that could ever reach the cap is
   the client timeline (`includeMatters`, spanning every matter of a person),
   and a list that came back at exactly the cap is shown as it came back —
   there is no truncation handling in 186, by the brief's own ruling. */
const FETCH_LIMIT = 200;

interface Props {
  caseId?: number | null;
  ccId?: number | null;
  clientId?: number | null;
  /** A person's list: the person's own rows ∪ the rows of the matters the person is linked to. */
  includeMatters?: boolean;
  canCreate: boolean;
  /** The archive (186 B.2): the app's archive verbs take the entity's EDIT bit (tasks.js, timeEntries.js). */
  canEdit: boolean;
  viewerId?: number | null;
  isAdmin?: boolean;
  /** `section`: the case views' collapsible csSec chrome. `card`: the client record's detail card. */
  chrome?: "section" | "card";
  anchorId?: string;
  sectionOpen?: boolean;
  onSectionToggle?: () => void;
}

const p2 = (n: number) => String(n).padStart(2, "0");
/** `dd.mm.yyyy · hh:mm`, in the reader's local time — 186 B.5: the dot between the day
 *  and the time is the one that already separates the time from the author. */
export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = [p2(d.getDate()), p2(d.getMonth() + 1), String(d.getFullYear())].join(".");
  return day + " · " + p2(d.getHours()) + ":" + p2(d.getMinutes());
}
/** 186 B.5 — the note's text sits under the WORD in the tag, not under the tag's border:
 *  `.tag` is `padding: 2px 6px` inside a 1px border (App.css), so the word starts 7px in. */
const TAG_TEXT_INSET = 7;
/** The local calendar day, `yyyy-mm-dd` — what DateField reads and writes. */
const localDay = (d: Date) => [String(d.getFullYear()), p2(d.getMonth() + 1), p2(d.getDate())].join("-");
/** The instant a chosen day means: today keeps the clock; another day is local noon. */
function occurredAtFor(day: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const now = new Date();
  if (day === localDay(now)) return now.toISOString();
  const d = new Date(day + "T12:00:00");
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
const byDateDesc = (a: InteractionRow, b: InteractionRow) =>
  b.occurredAt.localeCompare(a.occurredAt) || b.id - a.id;
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const ownerOf = (caseId?: number | null, ccId?: number | null, clientId?: number | null): InteractionOwner => {
  if (caseId != null) return { caseId };
  if (ccId != null) return { ccId };
  return { clientId: clientId ?? null };
};

export default function InteractionSection({
  caseId,
  ccId,
  clientId,
  includeMatters,
  canCreate,
  canEdit,
  viewerId,
  isAdmin,
  chrome = "section",
  anchorId = "ca-notes",
  sectionOpen = true,
  onSectionToggle,
}: Readonly<Props>) {
  const { t } = useTranslation() as { t: Translate };
  // 186 (17) — the app's one confirm-and-undo (useConfirmAndUndo, published on
  // App's context); used here for its UNDO half alone — see `archive`.
  const confirm = useConfirm();
  const owner = ownerOf(caseId, ccId, clientId);
  const hasOwner = caseId != null || ccId != null || clientId != null;
  const ownerKey = [caseId ?? "", ccId ?? "", clientId ?? "", includeMatters ? "1" : "0"].map(String).join("|");
  const showMatter = clientId != null && !!includeMatters;

  const [rows, setRows] = useState<InteractionRow[]>([]);
  const [loading, setLoading] = useState(hasOwner);
  const [error, setError] = useState<string | null>(null);
  const [seenKey, setSeenKey] = useState(ownerKey);

  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // 186 B.6 [RULED 186 (6)] — the search box's query, filtering the rows the
  // section HOLDS (B.7b) in the client: `text` and `title`, case-blind. No route
  // change — the earlier draft's `q` parameter had no reason left.
  const [q, setQ] = useState("");
  // 186 B.7b — the pager's state (the arithmetic runs over the held rows below).
  const pager = usePager();
  // 186 B.2 (b) [RULED 186 (12)] — the section's lock, DEFAULT LOCKED like every
  // lock in the case views (CaseView's collabLocked); the archive shows only
  // when unlocked. The lock is per mount and never persisted, as the others.
  const [locked, setLocked] = useState(true);

  // The owner changed under a mounted section: forget the previous owner's
  // rows during THIS render (React's documented shape), before the effect below
  // fetches the new one.
  if (seenKey !== ownerKey) {
    setSeenKey(ownerKey);
    setRows([]);
    setError(null);
    setLoading(hasOwner);
    pager.setPage(0);
  }

  // The rows, whenever the owner changes — and again whenever ANY mount
  // writes the store (183 E.2: `lx:reload-interactions`, fired by api.ts after
  // each write resolves — a note logged from the chat, a note written in
  // another mount). `alive` drops a late answer for an owner the user has
  // already left; every setState here runs after an await. A refetch replaces
  // the held rows.
  useEffect(() => {
    let alive = true;
    if (!hasOwner) return undefined;
    const load = () => {
      getInteractions(ownerOf(caseId, ccId, clientId), { includeMatters: !!includeMatters, limit: FETCH_LIMIT })
        .then((list) => {
          if (!alive) return;
          setRows([...list].sort(byDateDesc));
        })
        .catch((e: unknown) => {
          if (alive) setError(errText(e));
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    };
    load();
    const onChanged = () => {
      load();
    };
    globalThis.addEventListener(INTERACTIONS_CHANGED_EVENT, onChanged);
    return () => {
      alive = false;
      globalThis.removeEventListener(INTERACTIONS_CHANGED_EVENT, onChanged);
    };
  }, [caseId, ccId, clientId, includeMatters, hasOwner]);

  const save = async (body: string, day: string) => {
    if (!body || busy) return;
    const when = occurredAtFor(day);
    setBusy(true);
    setError(null);
    try {
      const row = await createInteractionNote(owner, body, when);
      setRows((prev) => [row, ...prev.filter((r) => r.id !== row.id)].sort(byDateDesc));
      setAddOpen(false);
    } catch (e: unknown) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  /* 186 B.2 / (17) — ARCHIVE WITH A FIVE-SECOND UNDO, and no question before the
     act: un-archiving has no screen in CV/CCV, so the undo is the safety.
     The house removal shape (App's `unlinkAllOf`): `confirmAndUndo` with
     `label: null` and no `message` SKIPS its confirm dialog and goes straight
     to the act + the UndoToast, whose window defaults to 5000ms and whose
     progress bar follows it — the hook couples confirm and undo only when a
     label or a message is passed, so this is its undo half alone. The act is
     the PUT `{ archived: true }` (the row leaves the live list — the server
     serves live rows only, B.1); Undo is the same PUT with `false`, the arm
     B.2a shipped with no caller on any screen — its first. Either PUT
     announces `lx:reload-interactions`, so every mounted section agrees. */
  const showError = (e: unknown) => {
    setError(errText(e));
  };
  const dropRow = (id: number) => {
    setRows((prev) => prev.filter((x) => x.id !== id));
  };
  const putRowBack = (snap: InteractionRow) => {
    setRows((prev) => [snap, ...prev.filter((x) => x.id !== snap.id)].sort(byDateDesc));
  };
  const archive = (r: InteractionRow) => {
    confirm({
      label: null,
      toastName: t("toast.archived", { fallback: "Archived" }),
      onConfirm: () => {
        dropRow(r.id);
        archiveInteractionNote(r.id).catch(showError);
      },
      undo: {
        snapshot: r,
        restore: (snap: InteractionRow) => {
          putRowBack(snap);
          unarchiveInteractionNote(snap.id).catch(showError);
        },
      },
    });
  };

  const mine = (r: InteractionRow) => isAdmin === true || (viewerId != null && r.authorId === viewerId);
  const originLabel = (o: string) => {
    if (o === "chat") return t("interaction.origin.chat", { fallback: "Chat" });
    return t("interaction.origin.note", { fallback: "Note" });
  };
  const directionWord = (d: string | null) => {
    if (d === "in") return t("interaction.direction.in", { fallback: "incoming" });
    if (d === "out") return t("interaction.direction.out", { fallback: "outgoing" });
    return undefined;
  };

  const renderTitle = (r: InteractionRow) => {
    if (!r.title) return null;
    if (r.docLink) {
      return (
        <a className="link" href={r.docLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: FS.s11 }}>
          {r.title}
        </a>
      );
    }
    return <div style={{ fontSize: FS.s11 }}>{r.title}</div>;
  };

  /* 186 B.4 [RULED 186 (6)] — A CHAT ROW IS ONE LINE: its text joins the meta
     line instead of sitting under it, truncated with an ellipsis, the whole text
     as the element's `title` so a hover shows it [PROPOSED → glance: the
     truncation width is the row's — the text takes what the tag, the date and
     the author leave]. A note row is unchanged. `oneLine` forced from outside
     is B.8's closed-section row, the same form for any origin. */
  const renderRow = (r: InteractionRow, oneLine = r.origin === "chat", withActions = true) => {
    const isNote = r.origin === "note";
    const showActions = withActions && isNote && mine(r) && canEdit && !locked;
    const pill = showMatter ? (r.caseRef ?? r.ccRef) : null;
    const meta = { fontSize: FS.s10, color: "var(--tm)", flexShrink: 0 } as const;
    return (
      <div
        key={r.id}
        className="ixRow"
        data-origin={r.origin}
        style={{ padding: "6px 0", borderBottom: "1px solid var(--br)" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: oneLine ? "nowrap" : "wrap", minWidth: 0, ...meta }}>
          <span className="tag" title={directionWord(r.direction)} style={{ flexShrink: 0 }}>
            {originLabel(r.origin)}
          </span>
          <span style={meta}>{fmtWhen(r.occurredAt)}</span>
          {r.authorName && <span style={meta}>· {r.authorName}</span>}
          {pill && (
            <span className="tag" title={r.caseTitle ?? r.ccTitle ?? undefined} style={{ flexShrink: 0 }}>
              {pill}
            </span>
          )}
          {oneLine && r.text && (
            <span
              title={r.text}
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: FS.s11,
                color: "var(--tp)",
              }}
            >
              {r.text}
            </span>
          )}
          {showActions && (
            <span style={{ marginLeft: "auto", display: "inline-flex", gap: 4 }}>
              {/* 186 B.2 — the app's own archive button: TimeEntryTable's (152 §2),
                  the ghost button in `--or` with the Archive icon, behind the lock. */}
              <button
                type="button"
                className="btn btn-ghost"
                title={t("common.archive", { fallback: "Archive" })}
                style={{ padding: "1px 4px", fontSize: FS.s9, color: "var(--or)" }}
                onClick={() => {
                  archive(r);
                }}
              >
                <Archive size={14} />
              </button>
            </span>
          )}
        </div>
        {renderTitle(r)}
        {!oneLine && r.text && (
          <div style={{ whiteSpace: "pre-wrap", fontSize: FS.s11, marginTop: 2, paddingLeft: TAG_TEXT_INSET }}>{r.text}</div>
        )}
      </div>
    );
  };

  // 186 B.6 — the filter, then 186 B.7b — the page over what it leaves, the
  // chronology's arithmetic.
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? rows.filter((r) => (r.text ?? "").toLowerCase().includes(needle) || (r.title ?? "").toLowerCase().includes(needle))
    : rows;
  const { pageItems, cur, tot } = pageOf(shown, pager.page);

  const body = (
    <>
      {addOpen && (
        <InteractionModal
          t={t}
          defaultDay={localDay(new Date())}
          busy={busy}
          onClose={() => {
            setAddOpen(false);
          }}
          onSave={(draft) => {
            void save(draft.text, draft.day);
          }}
        />
      )}
      {/* 186 B.6 — the app's search box, always present while the section is
          open, lock or no lock; 186 (c) [RULED 186 (16)] moved its markup to
          `SectionSearch`, the one field Notes, the Chronology and Stages share.
          At the list's left edge [PROPOSED → glance]: the brief said "aligned
          with the date column", but the tag before the date has no fixed width
          (Note / Chat / Notitie), so there is no date column to align to. */}
      <SectionSearch value={q} onChange={setQ} />
      {error && (
        <div role="alert" style={{ fontSize: FS.s10, color: "var(--rd)", margin: "4px 0" }}>
          {error}
        </div>
      )}
      {!loading && rows.length === 0 && !error && (
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
          {t("interaction.empty", { fallback: "Nothing has been noted on this file yet." })}
        </p>
      )}
      <div className="ixList">{pageItems.map((r) => renderRow(r))}</div>
      {tot > 1 && <Pager cur={cur} tot={tot} setPage={pager.setPage} />}
    </>
  );

  // 186 B.2 (b) / B.3 — the header's right content: "+ Add" (unlocked, and
  // `canCreate`; 57 §2 — AddButton supplies the "+ " itself, so the label is
  // the word alone) and the lock, exactly as the Tasks section carries them in
  // the case views (SectionHeader hides them while collapsed — the Phase 25.1
  // spine); the client record's card carries the same controls in its own
  // header row, TeamAccessPanel's shape.
  const controls = (
    <>
      <AddButton
        label={t("common.add_button.add", { fallback: "Add" })}
        visible={!locked && canCreate}
        onClick={() => {
          setAddOpen(true);
        }}
      />
      <LockButton
        locked={locked}
        onToggle={() => {
          setLocked((v) => !v);
        }}
      />
    </>
  );

  if (chrome === "card") {
    return (
      <div className="detail-card" id={anchorId}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <h4 style={{ margin: 0 }}>{t("caseView.notes.title", { fallback: "Notes" })}</h4>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}>{controls}</div>
        </div>
        {body}
      </div>
    );
  }
  /* 186 B.8 [RULED 186 (12)] — A CLOSED SECTION SHOWS ITS LAST ROW: the most
     recent interaction, in exactly B.4's one-line form (tag · date · author, the
     text truncated, the whole text on hover), whatever its origin — a chat row is
     the last interaction just as a note is [PROPOSED → glance] — and nothing
     else: no lock, no add, no search, no pager, no archive icon. It is CONTENT,
     rendered UNDER the collapsed header, never in `rightContent`: SectionHeader
     hides its controls while collapsed (the Phase 25.1 spine, `showControls`),
     and that rule is not fought. `rows[0]` is the most recent by the order the
     list uses (`occurredAt desc, id desc` — the store's `orderBy`, re-sorted
     here by `byDateDesc`); the rows are fetched on mount whether or not the
     section is open. An empty section shows the header alone. */
  const lastRow = !sectionOpen && rows.length > 0 ? rows[0] : null;
  return (
    <div className="csSec-notes" data-secollapsed={sectionOpen ? "0" : "1"}>
      <SectionHeader
        id={anchorId}
        title={t("caseView.notes.title", { fallback: "Notes" })}
        collapsible
        open={sectionOpen}
        onToggle={onSectionToggle}
        rightContent={controls}
      />
      {sectionOpen && body}
      {lastRow && <div className="ixList ixLast">{renderRow(lastRow, true, false)}</div>}
    </div>
  );
}
